import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  Grid,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search,
  FilterList,
  Download,
  Refresh,
  TrendingUp,
  TrendingDown,
  Receipt,
  Close,
  Person,
  Business,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import DataTable, { Column } from '../common/DataTable';
import { transactionService } from '../../services/transactionService';
import { useAppDispatch } from '../../hooks/redux';
import { addNotification } from '../../store/slices/uiSlice';

interface Transaction {
  id: string;
  type: 'INCOME' | 'EXPENSE';
  category: string;
  description: string;
  amount: number;
  gstAmount?: number;
  paymentMethod: string;
  status: string;
  date: string;
  referenceId?: string;
  referenceType?: string;
  customerId?: string;
  customerName?: string;
  staffId?: string;
  staffName?: string;
  receiptNumber?: string;
  vendorName?: string;
  createdAt: string;
  updatedAt: string;
}

interface TransactionFilters {
  type?: 'INCOME' | 'EXPENSE';
  category?: string;
  paymentMethod?: string;
  status?: string;
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
  minAmount?: number;
  maxAmount?: number;
  search?: string;
}

const TransactionList: React.FC = () => {
  const dispatch = useAppDispatch();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState<TransactionFilters>({});
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const columns: Column[] = [
    {
      id: 'date',
      label: 'Date',
      format: (value: any, row: any) => {
        const transaction = row as Transaction;
        return (
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {dayjs(transaction.date).format('MMM DD, YYYY')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayjs(transaction.date).format('hh:mm A')}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: 'type',
      label: 'Type',
      format: (value: any, row: any) => {
        const transaction = row as Transaction;
        return (
          <Chip
            icon={transaction.type === 'INCOME' ? <TrendingUp /> : <TrendingDown />}
            label={transaction.type}
            color={transaction.type === 'INCOME' ? 'success' : 'error'}
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      id: 'description',
      label: 'Description',
      format: (value: any, row: any) => {
        const transaction = row as Transaction;
        return (
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {transaction.description}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {transaction.category}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: 'amount',
      label: 'Amount',
      format: (value: any, row: any) => {
        const transaction = row as Transaction;
        return (
          <Box textAlign="right">
            <Typography
              variant="body2"
              fontWeight={700}
              color={transaction.type === 'INCOME' ? 'success.main' : 'error.main'}
            >
              {transaction.type === 'INCOME' ? '+' : '-'}₹{transaction.amount.toLocaleString()}
            </Typography>
            {transaction.gstAmount && (
              <Typography variant="caption" color="text.secondary">
                GST: ₹{transaction.gstAmount.toLocaleString()}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      id: 'paymentMethod',
      label: 'Payment Method',
      format: (value: any, row: any) => {
        const transaction = row as Transaction;
        return (
          <Chip
            label={transaction.paymentMethod}
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      id: 'reference',
      label: 'Reference',
      format: (value: any, row: any) => {
        const transaction = row as Transaction;
        return (
          <Box>
            {transaction.customerName && (
              <Box display="flex" alignItems="center" mb={0.5}>
                <Person fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                <Typography variant="caption">
                  {transaction.customerName}
                </Typography>
              </Box>
            )}
            {transaction.staffName && (
              <Box display="flex" alignItems="center" mb={0.5}>
                <Person fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                <Typography variant="caption">
                  {transaction.staffName}
                </Typography>
              </Box>
            )}
            {transaction.vendorName && (
              <Box display="flex" alignItems="center" mb={0.5}>
                <Business fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                <Typography variant="caption">
                  {transaction.vendorName}
                </Typography>
              </Box>
            )}
            {transaction.receiptNumber && (
              <Box display="flex" alignItems="center">
                <Receipt fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                <Typography variant="caption">
                  {transaction.receiptNumber}
                </Typography>
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      id: 'status',
      label: 'Status',
      format: (value: any, row: any) => {
        const transaction = row as Transaction;
        return (
          <Chip
            label={transaction.status}
            size="small"
            color={
              transaction.status === 'PAID' || transaction.status === 'APPROVED'
                ? 'success'
                : transaction.status === 'PENDING'
                ? 'warning'
                : 'error'
            }
            variant="outlined"
          />
        );
      },
    },
  ];

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
        startDate: filters.startDate?.toISOString(),
        endDate: filters.endDate?.toISOString(),
      };

      const response = await transactionService.getTransactions(params);
      setTransactions(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages,
      }));
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to fetch transactions',
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [pagination.page, pagination.limit]);

  const handleFilterChange = (key: keyof TransactionFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchTransactions();
  };

  const handleClearFilters = () => {
    setFilters({});
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchTransactions();
  };

  const handleRowClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setDetailsOpen(true);
  };

  const handleExport = async () => {
    try {
      // Implementation for export functionality
      dispatch(addNotification({
        type: 'info',
        message: 'Export functionality will be implemented soon',
      }));
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to export transactions',
      }));
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Transaction History
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleExport}
            >
              Export
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchTransactions}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Search"
                  placeholder="Search transactions..."
                  value={filters.search || ''}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={filters.type || ''}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    label="Type"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="INCOME">Income</MenuItem>
                    <MenuItem value="EXPENSE">Expense</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={filters.paymentMethod || ''}
                    onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                    label="Payment Method"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="CASH">Cash</MenuItem>
                    <MenuItem value="CARD">Card</MenuItem>
                    <MenuItem value="UPI">UPI</MenuItem>
                    <MenuItem value="ONLINE">Online</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(date) => handleFilterChange('startDate', date)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <DatePicker
                  label="End Date"
                  value={filters.endDate}
                  onChange={(date) => handleFilterChange('endDate', date)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} md={1}>
                <Box display="flex" gap={1} height="100%" alignItems="center">
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    sx={{ minWidth: 'auto', px: 2 }}
                  >
                    <FilterList />
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    sx={{ minWidth: 'auto', px: 2 }}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Transactions Table */}
        <DataTable
          columns={columns}
          rows={transactions}
          loading={loading}
          page={pagination.page - 1}
          rowsPerPage={pagination.limit}
          totalCount={pagination.total}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page: page + 1 }))}
          onRowsPerPageChange={(limit) => setPagination(prev => ({ ...prev, limit, page: 1 }))}
          onView={handleRowClick}
        />

        {/* Transaction Details Dialog */}
        <Dialog
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Transaction Details</Typography>
              <IconButton onClick={() => setDetailsOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedTransaction && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Basic Information
                    </Typography>
                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Transaction ID
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {selectedTransaction.id}
                      </Typography>
                    </Box>
                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Type
                      </Typography>
                      <Chip
                        icon={selectedTransaction.type === 'INCOME' ? <TrendingUp /> : <TrendingDown />}
                        label={selectedTransaction.type}
                        color={selectedTransaction.type === 'INCOME' ? 'success' : 'error'}
                        size="small"
                      />
                    </Box>
                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Category
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {selectedTransaction.category}
                      </Typography>
                    </Box>
                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Description
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {selectedTransaction.description}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Financial Details
                    </Typography>
                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Amount
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        color={selectedTransaction.type === 'INCOME' ? 'success.main' : 'error.main'}
                      >
                        {selectedTransaction.type === 'INCOME' ? '+' : '-'}₹{selectedTransaction.amount.toLocaleString()}
                      </Typography>
                    </Box>
                    {selectedTransaction.gstAmount && (
                      <Box mb={2}>
                        <Typography variant="body2" color="text.secondary">
                          GST Amount
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          ₹{selectedTransaction.gstAmount.toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Payment Method
                      </Typography>
                      <Chip
                        label={selectedTransaction.paymentMethod}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Status
                      </Typography>
                      <Chip
                        label={selectedTransaction.status}
                        size="small"
                        color={
                          selectedTransaction.status === 'PAID' || selectedTransaction.status === 'APPROVED'
                            ? 'success'
                            : selectedTransaction.status === 'PENDING'
                            ? 'warning'
                            : 'error'
                        }
                      />
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Additional Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box mb={2}>
                          <Typography variant="body2" color="text.secondary">
                            Date
                          </Typography>
                          <Typography variant="body1" fontWeight={600}>
                            {dayjs(selectedTransaction.date).format('MMMM DD, YYYY hh:mm A')}
                          </Typography>
                        </Box>
                        {selectedTransaction.receiptNumber && (
                          <Box mb={2}>
                            <Typography variant="body2" color="text.secondary">
                              Receipt Number
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {selectedTransaction.receiptNumber}
                            </Typography>
                          </Box>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        {selectedTransaction.customerName && (
                          <Box mb={2}>
                            <Typography variant="body2" color="text.secondary">
                              Customer
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {selectedTransaction.customerName}
                            </Typography>
                          </Box>
                        )}
                        {selectedTransaction.staffName && (
                          <Box mb={2}>
                            <Typography variant="body2" color="text.secondary">
                              Staff
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {selectedTransaction.staffName}
                            </Typography>
                          </Box>
                        )}
                        {selectedTransaction.vendorName && (
                          <Box mb={2}>
                            <Typography variant="body2" color="text.secondary">
                              Vendor
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {selectedTransaction.vendorName}
                            </Typography>
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default TransactionList;